import { ApiResponse } from '@roc/client-portal-shared/services';
import { ScopeOfWorkV2Service } from './scopeOfWorkV2Service';
import { SecureLinkRequest } from './secureLinkScopeOfWorkRevisionService';

/**
 * Scope of work calls for the secure link pages: loan-service's @SecureLinkEndpoint endpoints, authorized by the
 * secure link session through its request() (Authorization: Bearer). Never sends a scope of work id, lender id or
 * username: the server takes them from the session.
 *
 * Both services replace the public ScopeOfWorkV2Service in the existing stores (ScopeOfWorkV2Store.setScopeOfWorkService),
 * so they keep its method signatures and answer like axios did ({ data } holding what nportal returned); the ids the
 * stores still pass are ignored. Whatever they don't override (categories, new template, export) stays on the public
 * nportal endpoints, which read nothing stored.
 */
const SHARE_BASE = '/api/v1/loan/secure-link/scope-of-work';
const CREATE_BASE = '/api/v1/loan/secure-link/create-scope-of-work';

const toApiResponse = (data: any) => new ApiResponse(data, undefined);

/** The share link (/link/scope-of-work, purpose SOW_SHARE): the scope of work comes from the session. */
export class SecureLinkScopeOfWorkService extends ScopeOfWorkV2Service {
  constructor(private readonly request: SecureLinkRequest) {
    super();
  }

  getScopeOfWork(lenderId: string, scopeOfWorkId: string) {
    return this.request(`${SHARE_BASE}/data`).then(toApiResponse);
  }

  getSplittedCategories(scopeOfWorkId: string, lenderId: string) {
    return this.request(`${SHARE_BASE}/split-categories`).then(toApiResponse);
  }

  getSowSubmissionErrors(loanId: string, propertyId: any, data: any, version: number) {
    return this.request(`${SHARE_BASE}/submission-errors?version=${encodeURIComponent(version)}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(toApiResponse);
  }

  saveSowAuditLogs(scopeOfWorkId: string, lenderId, data: any, isSubmit: boolean, userName: string) {
    const userNameParam = userName ? `&userName=${encodeURIComponent(userName)}` : '';
    return this.request(`${SHARE_BASE}/audit-logs?isSubmit=${isSubmit}${userNameParam}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(toApiResponse);
  }

  saveScopeOfWork(lenderId: string, scopeOfWorkId: string, username: string, data: any) {
    return this.request(`${SHARE_BASE}/save`, { method: 'POST', body: JSON.stringify(data) }).then(toApiResponse);
  }

  saveScopeOfWorkDataContent(lenderId: string, scopeOfWorkId: string, username: string, data: any) {
    return this.request(`${SHARE_BASE}/save-data-content`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(toApiResponse);
  }

  submitScopeOfWork(lenderId: string, scopeOfWorkId: string, username: string, data: any) {
    return this.request(`${SHARE_BASE}/submit`, { method: 'POST', body: JSON.stringify(data) }).then(toApiResponse);
  }

  /** Multipart: the scope of work pdf as `file`. The browser sets the multipart Content-Type. */
  savePdf(file: FormData, scopeOfWorkId, lenderId) {
    return this.request(`${SHARE_BASE}/pdf`, { method: 'POST', body: file }).then(toApiResponse);
  }

  markSOWAsInReview(reviewStatus: string) {
    const reviewStatusParam = reviewStatus ? `?reviewStatus=${encodeURIComponent(reviewStatus)}` : '';
    return this.request(`${SHARE_BASE}/mark-in-review${reviewStatusParam}`, { method: 'POST' }).then(toApiResponse);
  }

  markSOWAsMoreInfoNeeded() {
    return this.request(`${SHARE_BASE}/more-info`, { method: 'POST' }).then(toApiResponse);
  }
}

/**
 * The create link (/link/create-scope-of-work, purpose SOW_CREATE): only the submit goes through the session, which
 * holds the lender and the lender user. Template, categories, submission errors and export stay public.
 */
export class SecureLinkCreateScopeOfWorkService extends ScopeOfWorkV2Service {
  constructor(private readonly request: SecureLinkRequest) {
    super();
  }

  submitScopeOfWorkPublic(host: string, scopeOfWorkId: string, data: any) {
    return this.request(`${CREATE_BASE}/submit`, { method: 'POST', body: JSON.stringify(data) }).then(toApiResponse);
  }
}
